import type { LiveCapabilitiesResponse } from '../types/auth';

/**
 * CORE-817 — the capability client: loads the caller's effective Live rights,
 * caches them per user, refreshes them (after a level-up, on focus/interval)
 * and degrades safely when the source is unavailable. It is the FOUNDATION of
 * the live-unification bundle — every screen that gates an affordance reads the
 * rights this client provides.
 *
 * Deliberately pure (no React, no `api` import): the cache and degradation
 * behaviour — the parts with the acceptance criteria — are unit-tested here
 * with a fake fetcher. `src/services/liveCapabilitiesStore.ts` wires the real
 * `api.getCreatorCapabilities`; `src/hooks/useLiveCapabilities.ts` binds it to
 * auth + the `unified_live_session` flag + focus/interval refresh.
 *
 * Two hard rules from CORE-769 shape this file:
 *  - The client only READS `capabilities[]`. It never derives a right from a
 *    level code or role. `SAFE_BASELINE_CAPABILITIES` is a documented FLAT
 *    fallback list (the backend `ForUser` set), not a level→rights matrix —
 *    there is no duplicated capability matrix on the front end.
 *  - The dynamic pre-create answer (this client) and the frozen per-session
 *    snapshot (`RoomResponse.capabilities`) are different things and must not
 *    be substituted for one another — this client owns only the former.
 */

/**
 * The safe baseline: the backend `LiveSessionCapabilities.ForUser` set — the
 * rights EVERY session creator holds. Served when the capability API errors or
 * the feature flag is off. Chosen as the middle ground the ticket demands:
 * NOT «everything» (would show buttons the server rejects) and NOT «nothing»
 * (would break users who do have rights). A flat list, not a matrix.
 */
export const SAFE_BASELINE_CAPABILITIES: readonly string[] = [
  'session.create.instant',
  'session.create.from_dm',
  'session.visibility.private',
  'session.invite.link',
  'session.invite.during_session',
  'session.guest.entry',
  'session.end.manual',
  'session.participants.kick',
  'session.chat.basic',
];

/**
 * A fresh baseline `LiveCapabilitiesResponse`. Role `user`, no level, level
 * layer off — an honest description of «we could not read your real rights, so
 * here is the floor». A new object (and a new array) each call so no caller can
 * mutate the shared constant.
 */
export function safeBaselineCapabilities(): LiveCapabilitiesResponse {
  return {
    contractVersion: 1,
    dynamic: true,
    creatorRole: 'user',
    creatorLevel: null,
    levelLayerEnabled: false,
    capabilities: [...SAFE_BASELINE_CAPABILITIES],
    locked: [],
  };
}

/**
 * The cache key. It MUST include the user so another account's rights can never
 * survive a switch (an acceptance criterion), and it carries an optional
 * session scope so the same store can key a per-session read distinctly from
 * the pre-create preview without them colliding. `sessionId` omitted → the
 * pre-create preview namespace.
 */
export function capabilityCacheKey(
  userId: string | null | undefined,
  sessionId?: string | number | null,
): string {
  const user = userId == null || userId === '' ? 'anon' : String(userId);
  const session = sessionId == null || sessionId === '' ? 'pre-create' : String(sessionId);
  return `${user}::${session}`;
}

export type CapabilitiesFetcher = (userId: string) => Promise<LiveCapabilitiesResponse>;

export interface LoadOptions {
  /**
   * The `unified_live_session` gate. OFF → the client returns the safe baseline
   * WITHOUT a network call (CORE-769 §7): the snapshot is not to be trusted, so
   * there is nothing to fetch.
   */
  flagEnabled: boolean;
  /** Optional session scope; omitted for the pre-create preview. */
  sessionId?: string | number | null;
  /** Bypass a cached entry and re-fetch (focus / interval / post level-up). */
  force?: boolean;
}

export interface LiveCapabilitiesStore {
  /** Synchronous read of the cached value, or null if nothing cached yet. */
  peek(userId: string | null | undefined, sessionId?: string | number | null): LiveCapabilitiesResponse | null;
  /**
   * Откуда взято закэшированное значение.
   *
   * `safeBaselineCapabilities()` неотличим от настоящего ответа сервера про
   * обычного пользователя: те же поля, тот же `dynamic: true`. Пока эту разницу
   * нельзя прочитать, любой экран, который прячет что-то по правам, при
   * выключенном флаге или ошибке API покажет мастеру интерфейс базового
   * пользователя — так и случилось с визардом создания.
   *
   * `'server'` — ответ дошёл; `'baseline'` — подставлен пол (флаг выключен,
   * пользователя нет, запрос упал); `null` — в кэше пусто.
   */
  source(userId: string | null | undefined, sessionId?: string | number | null): 'server' | 'baseline' | null;
  /** Load rights: cache-first unless `force`, safe-baseline on flag-off/error. */
  load(userId: string | null | undefined, opts: LoadOptions): Promise<LiveCapabilitiesResponse>;
  /** Drop cached rights (all, or one user) and notify — the next load refetches. */
  invalidate(userId?: string | null, sessionId?: string | number | null): void;
  /** Drop everything (e.g. on logout) and notify. */
  clear(): void;
  /** Subscribe to refreshes (a load resolved, or an invalidate/clear happened). */
  subscribe(callback: () => void): () => void;
}

/**
 * Build a capability store over a fetcher. The store keeps a module-style cache
 * (here scoped to the instance so tests are isolated) plus in-flight de-dup so
 * concurrent loads for the same key share one request.
 */
export function createLiveCapabilitiesStore(fetcher: CapabilitiesFetcher): LiveCapabilitiesStore {
  const cache = new Map<string, LiveCapabilitiesResponse>();
  // Провенанс каждой записи кэша — см. `source` в интерфейсе.
  const origin = new Map<string, 'server' | 'baseline'>();
  const inflight = new Map<string, Promise<LiveCapabilitiesResponse>>();
  const subscribers = new Set<() => void>();

  function notify(): void {
    subscribers.forEach((cb) => cb());
  }

  function peek(
    userId: string | null | undefined,
    sessionId?: string | number | null,
  ): LiveCapabilitiesResponse | null {
    return cache.get(capabilityCacheKey(userId, sessionId)) ?? null;
  }

  function source(
    userId: string | null | undefined,
    sessionId?: string | number | null,
  ): 'server' | 'baseline' | null {
    return origin.get(capabilityCacheKey(userId, sessionId)) ?? null;
  }

  function load(
    userId: string | null | undefined,
    opts: LoadOptions,
  ): Promise<LiveCapabilitiesResponse> {
    const key = capabilityCacheKey(userId, opts.sessionId);

    // Flag off (or no user) → safe baseline, no network. We still write it to
    // the cache so a synchronous peek is consistent with the async result.
    if (!opts.flagEnabled || userId == null || userId === '') {
      const baseline = safeBaselineCapabilities();
      cache.set(key, baseline);
      origin.set(key, 'baseline');
      return Promise.resolve(baseline);
    }

    if (!opts.force && cache.has(key)) {
      return Promise.resolve(cache.get(key)!);
    }

    const existing = inflight.get(key);
    if (existing) return existing;

    const request = fetcher(String(userId))
      .then((res) => {
        // FULL replace, never a merge: a right the server dropped must vanish,
        // so a stale cache can never keep showing a now-unavailable feature.
        cache.set(key, res);
        origin.set(key, 'server');
        inflight.delete(key);
        notify();
        return res;
      })
      .catch(() => {
        // Degrade to the safe baseline — not «all», not «nothing». Overwriting
        // any stale value is deliberate: the baseline is the floor, and keeping
        // a stale wider set could show a feature that is no longer available.
        const baseline = safeBaselineCapabilities();
        cache.set(key, baseline);
        origin.set(key, 'baseline');
        inflight.delete(key);
        notify();
        return baseline;
      });

    inflight.set(key, request);
    return request;
  }

  function invalidate(userId?: string | null, sessionId?: string | number | null): void {
    if (userId == null) {
      cache.clear();
      origin.clear();
      inflight.clear();
    } else if (sessionId !== undefined) {
      const key = capabilityCacheKey(userId, sessionId);
      cache.delete(key);
      origin.delete(key);
      inflight.delete(key);
    } else {
      // Drop every scope belonging to this user (pre-create + any session).
      const prefix = `${String(userId)}::`;
      for (const key of Array.from(cache.keys())) {
        if (key.startsWith(prefix)) { cache.delete(key); origin.delete(key); }
      }
      for (const key of Array.from(inflight.keys())) {
        if (key.startsWith(prefix)) inflight.delete(key);
      }
    }
    notify();
  }

  function clear(): void {
    cache.clear();
    origin.clear();
    inflight.clear();
    notify();
  }

  function subscribe(callback: () => void): () => void {
    subscribers.add(callback);
    return () => {
      subscribers.delete(callback);
    };
  }

  return { peek, source, load, invalidate, clear, subscribe };
}

/**
 * Что хук отдаёт наружу ОДНИМ значением — и к какому пользователю это значение
 * относится. `userId` здесь не справочный: по нему `snapshotForUser` узнаёт,
 * что состояние принадлежит прошлому аккаунту, и не отдаёт его новому.
 */
export interface CapabilitiesSnapshot {
  userId: string | null;
  data: LiveCapabilitiesResponse;
  /** Для этого пользователя в кэше пусто — ответ ещё не приходил. */
  loading: boolean;
  /** См. `LiveCapabilitiesStore.source`: `true` — в `data` ответ сервера. */
  fromServer: boolean;
}

/**
 * Синхронный снимок прав пользователя из стора: его кэш, а если пусто — safe
 * baseline с `loading: true`. Только чтение, без запроса — запрос запускает
 * хук в эффекте.
 */
export function capabilitiesSnapshot(
  store: Pick<LiveCapabilitiesStore, 'peek' | 'source'>,
  userId: string | null,
): CapabilitiesSnapshot {
  const cached = store.peek(userId);
  return {
    userId,
    data: cached ?? safeBaselineCapabilities(),
    loading: cached == null,
    fromServer: store.source(userId) === 'server',
  };
}

/**
 * Снимок, который можно отдать для `userId` прямо сейчас. Состояние того же
 * пользователя возвращается как есть (та же ссылка — лишних рендеров нет).
 * Состояние ДРУГОГО пользователя не годится ни на один рендер: пока хук
 * подменял `data` только в `.then()` форсированной загрузки, визард между
 * сменой аккаунта и ответом сервера жил с правами предыдущего аккаунта — и
 * показал «Запланировать» тому, у кого нет `session.schedule`. Поэтому чужое
 * состояние заменяется снимком нового пользователя из стора: его собственным
 * кэшем (ранний вход в той же вкладке) или полом с `loading: true`.
 */
export function snapshotForUser(
  state: CapabilitiesSnapshot,
  userId: string | null,
  store: Pick<LiveCapabilitiesStore, 'peek' | 'source'>,
): CapabilitiesSnapshot {
  return state.userId === userId ? state : capabilitiesSnapshot(store, userId);
}
